import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { printReceipt as sendPrintReceipt } from '../../../../services/localPrintService';

// ═══════════════════════════════════════════════════════════
// 🍳 MUTFAK FİŞİ YAZDIRMA SERVİSİ
// ═══════════════════════════════════════════════════════════
// Bu servis sadece mutfak fişi yazdırmak için kullanılır.
// Normal satış fişinden farklı olarak:
// - Sadece ürünler ve adetleri gösterilir
// - Sipariş notu gösterilir (varsa)
// - Fiyat, KDV, toplam gibi bilgiler YOK
// - Firma bilgisi ve masa/sipariş numarası gösterilir

// ═══════════════════════════════════════════════════════════
// 📦 TYPE DEFINITIONS
// ═══════════════════════════════════════════════════════════

// Mutfak fişi için alt ürün tipi (menü içerikleri)
export interface KitchenOrderSubItem {
  id: string;
  name: string;
  quantity: number;
  isExtraOption?: boolean; // Ekstra opsiyon mu (boy değişimi gibi)
}

// Mutfak fişi için ürün tipi
export interface KitchenOrderItem {
  id: string;
  name: string;
  quantity: number;
  note?: string;
  categoryName?: string;
  categoryId?: string;
  subItems?: KitchenOrderSubItem[];
}

// Mutfak fişi verisi
export interface KitchenReceiptData {
  orderNumber: string; // Sipariş numarası (HS-XXXXXX veya masa adı)
  tableName?: string; // Masa adı (opsiyonel)
  waiterName?: string; // Garson adı (opsiyonel)
  items: KitchenOrderItem[];
  orderNote?: string; // Genel sipariş notu
  companyName?: string; // Firma adı
  orderTime?: string; // Sipariş zamanı
  isAdditionalOrder?: boolean; // ✅ İlave sipariş mi? (true = "İLAVE SİPARİŞ" başlığı gösterilir)
  isCancellationOrder?: boolean; // ✅ YENİ: İptal siparişi mi? (true = "İPTAL SİPARİŞ" başlığı gösterilir)
}

// Yazıcı tanımlama için tip
interface DefinedPrinter {
  id: string;
  name: string;
}

// Servis ayarları
interface KitchenPrintServiceProps {
  companyId: string | null;
  screenId: string | null;
  screenSettings?: any; // SQL'den çekilen ekran ayarları (PrintingSettingsJson)
  onPrintError?: (error: string) => void;
  onPrintSuccess?: (message: string) => void;
}

// ✅ YENİ: Kategori bazlı yazdırma sonucu (atanmamış kategoriler skip edilir)
export interface KitchenPrintSkippedItem {
  name: string;
  quantity: number;
  categoryName?: string;
  categoryId?: string;
}

export interface KitchenPrintResult {
  /** Yazıcıya gönderilen ürünler (kategorisi atanmış olanlar) */
  printedItems: KitchenOrderItem[];
  /** Atlanan ürünler (kategorisinde yazıcı atanmamış) — yazdırılmaz */
  skippedItems: KitchenPrintSkippedItem[];
  /** Atlanan ürünlerin benzersiz kategori adları (UI uyarısı için) */
  skippedCategoryNames: string[];
  /** Hiçbir ürün yazdırılmadı mı? (sepetteki TÜM ürünler atlandı) */
  allSkipped: boolean;
  /** Yazdırılacak ürün hiç yok muydu? (boş sepet) */
  nothingToPrint: boolean;
}

// Hook dönüş tipi
interface KitchenPrintServiceReturn {
  printKitchenReceipt: (receiptData: KitchenReceiptData) => Promise<void>;
  printKitchenReceiptByCategory: (receiptData: KitchenReceiptData) => Promise<KitchenPrintResult>;
}

// ═══════════════════════════════════════════════════════════
// 🖨️ CONSTANTS
// ═══════════════════════════════════════════════════════════

// Yerel yazıcı servisi endpoint (varsayılan, screenSettings'den override edilebilir)
const DEFAULT_LOCAL_SERVICE_URL = 'http://localhost:3003';

// ═══════════════════════════════════════════════════════════
// 📝 HTML TEMPLATE FUNCTIONS
// ═══════════════════════════════════════════════════════════

/**
 * Mutfak fişi HTML şablonunu oluşturur
 * Sadece ürün listesi, adetler ve sipariş notu içerir
 * Fiyat, KDV gibi bilgiler YOK
 */
const createKitchenReceiptHtml = (receiptData: KitchenReceiptData): string => {
  const currentTime = receiptData.orderTime || new Date().toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Mutfak Fişi</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 9pt;
          width: 72mm;
          max-width: 72mm;
          margin: 0;
          padding: 1mm 2mm 2mm 2mm;
          box-sizing: border-box;
          line-height: 1.2;
        }
        .header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 4px;
          margin-bottom: 4px;
        }
        .header h1 {
          margin: 0 0 2px 0;
          font-size: 11pt;
          font-weight: bold;
          text-align: center;
        }
        .header .company-name {
          font-size: 9pt;
          margin: 0;
          font-weight: normal;
          text-align: center;
        }
        .order-info {
          text-align: center;
          margin-bottom: 6px;
        }
        .order-number {
          font-size: 9pt;
          font-weight: bold;
          letter-spacing: 0;
          margin: 2px auto;
          padding: 2px 4px;
          display: inline-block;
          text-align: center;
        }
        .table-name {
          font-size: 18pt;
          font-weight: 900;
          margin: 4px 0;
          padding: 4px 0;
          color: #000;
          text-align: center;
          border-top: 2px solid #000;
          border-bottom: 2px solid #000;
        }
        .waiter-name {
          font-size: 10pt;
          color: #000;
          font-weight: bold;
          margin: 2px 0;
          text-align: center;
        }
        .order-time {
          font-size: 9pt;
          color: #000;
          font-weight: bold;
          margin: 2px 0;
          text-align: center;
        }
        .items-section {
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
          padding: 4px 0;
          margin: 4px 0;
        }
        .item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 4px;
          padding-bottom: 3px;
          border-bottom: 1px dotted #999;
        }
        .item:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }
        .item-quantity {
          font-size: 12pt;
          font-weight: bold;
          min-width: 24px;
          text-align: center;
          background-color: #000;
          color: #fff;
          padding: 1px 3px;
          margin-right: 5px;
        }
        .item-details {
          flex: 1;
          min-width: 0;
        }
        .item-name {
          font-size: 10pt;
          font-weight: bold;
          margin: 0;
          word-wrap: break-word;
        }
        .item-note {
          font-size: 8pt;
          color: #000;
          font-style: italic;
          margin: 2px 0 0 0;
          padding-left: 3px;
          border-left: 1px solid #000;
        }
        .sub-items {
          margin-left: 29px;
          margin-top: 2px;
        }
        .sub-item {
          font-size: 8pt;
          color: #000;
          margin: 1px 0;
          padding-left: 5px;
          border-left: 2px solid #666;
        }
        .sub-item-name {
          margin: 0;
        }
        .sub-item.extra-option {
          font-weight: bold;
          border-left-color: #000;
        }
        .order-note {
          border: 1px solid #000;
          padding: 4px;
          margin: 4px 0;
        }
        .order-note-title {
          font-size: 8pt;
          font-weight: bold;
          margin: 0 0 2px 0;
          color: #000;
        }
        .order-note-text {
          font-size: 9pt;
          margin: 0;
          font-style: italic;
        }
        .footer {
          text-align: center;
          margin-top: 4px;
          padding-top: 4px;
          border-top: 1px dashed #000;
        }
        .footer p {
          margin: 2px 0;
          font-size: 9pt;
          color: #000;
          font-weight: bold;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${receiptData.isCancellationOrder ? '❌ İPTAL SİPARİŞ ❌' : receiptData.isAdditionalOrder ? '⚠️ İLAVE SİPARİŞ ⚠️' : 'MUTFAK SİPARİŞİ'}</h1>
        ${receiptData.companyName ? `<p class="company-name">${receiptData.companyName}</p>` : ''}
      </div>
      
      <div class="order-info">
        ${receiptData.tableName ? `<div class="table-name">${receiptData.tableName}</div>` : ''}
        <div class="order-number">${receiptData.orderNumber}</div>
        ${receiptData.waiterName ? `<div class="waiter-name">Garson: ${receiptData.waiterName}</div>` : ''}
        <div class="order-time">${currentTime}</div>
      </div>
      
      <div class="items-section">
        ${receiptData.items.map(item => `
          <div class="item">
            <div class="item-quantity">${item.quantity}</div>
            <div class="item-details">
              <p class="item-name">${item.name}</p>
              ${item.note ? `<p class="item-note">${item.note}</p>` : ''}
              ${item.subItems && item.subItems.length > 0 ? `
                <div class="sub-items">
                  ${item.subItems.map(subItem => `
                    <div class="sub-item${subItem.isExtraOption ? ' extra-option' : ''}">
                      <p class="sub-item-name">- ${subItem.name}${subItem.quantity > 1 ? ` x${subItem.quantity}` : ''}</p>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        `).join('')}
      </div>
      
      ${receiptData.orderNote ? `
        <div class="order-note">
          <p class="order-note-title">SİPARİŞ NOTU:</p>
          <p class="order-note-text">${receiptData.orderNote}</p>
        </div>
      ` : ''}
      
      <div class="footer">
        <p>${receiptData.items.length} kalem / ${receiptData.items.reduce((sum, item) => sum + item.quantity, 0)} adet</p>
      </div>
    </body>
    </html>
  `;
};

/**
 * Kategori bazlı mutfak fişi HTML şablonunu oluşturur
 * Her kategori için ayrı bölüm oluşturur
 */
const createKitchenReceiptByCategoryHtml = (
  receiptData: KitchenReceiptData,
  categoryName: string,
  categoryItems: KitchenOrderItem[]
): string => {
  const currentTime = receiptData.orderTime || new Date().toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Mutfak Fişi - ${categoryName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Poppins', sans-serif;
          font-size: 9pt;
          width: 72mm;
          max-width: 72mm;
          margin: 0;
          padding: 1mm 2mm 2mm 2mm;
          box-sizing: border-box;
          line-height: 1.2;
        }
        .header {
          text-align: center;
          border-bottom: 1px dashed #000;
          padding-bottom: 4px;
          margin-bottom: 4px;
        }
        .header h1 {
          margin: 0 0 2px 0;
          font-size: 11pt;
          font-weight: bold;
          text-align: center;
        }
        .category-badge {
          background-color: #000;
          color: #fff;
          padding: 2px 8px;
          font-size: 9pt;
          font-weight: bold;
          display: inline-block;
          margin: 2px 0;
          text-align: center;
        }
        .order-info {
          text-align: center;
          margin-bottom: 6px;
        }
        .order-number {
          font-size: 9pt;
          font-weight: bold;
          letter-spacing: 0;
          margin: 2px auto;
          padding: 2px 4px;
          display: inline-block;
          text-align: center;
        }
        .table-name {
          font-size: 18pt;
          font-weight: 900;
          margin: 4px 0;
          padding: 4px 0;
          color: #000;
          text-align: center;
          border-top: 2px solid #000;
          border-bottom: 2px solid #000;
        }
        .waiter-name {
          font-size: 10pt;
          color: #000;
          font-weight: bold;
          margin: 2px 0;
          text-align: center;
        }
        .order-time {
          font-size: 9pt;
          color: #000;
          font-weight: bold;
          margin: 2px 0;
          text-align: center;
        }
        .items-section {
          border-top: 1px dashed #000;
          border-bottom: 1px dashed #000;
          padding: 4px 0;
          margin: 4px 0;
        }
        .item {
          display: flex;
          align-items: flex-start;
          margin-bottom: 4px;
          padding-bottom: 3px;
          border-bottom: 1px dotted #999;
        }
        .item:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }
        .item-quantity {
          font-size: 12pt;
          font-weight: bold;
          min-width: 24px;
          text-align: center;
          background-color: #000;
          color: #fff;
          padding: 1px 3px;
          margin-right: 5px;
        }
        .item-details {
          flex: 1;
          min-width: 0;
        }
        .item-name {
          font-size: 10pt;
          font-weight: bold;
          margin: 0;
          word-wrap: break-word;
        }
        .item-note {
          font-size: 8pt;
          color: #000;
          font-style: italic;
          margin: 2px 0 0 0;
          padding-left: 3px;
          border-left: 1px solid #000;
        }
        .sub-items {
          margin-left: 29px;
          margin-top: 2px;
        }
        .sub-item {
          font-size: 8pt;
          color: #000;
          margin: 1px 0;
          padding-left: 5px;
          border-left: 2px solid #666;
        }
        .sub-item-name {
          margin: 0;
        }
        .sub-item.extra-option {
          font-weight: bold;
          border-left-color: #000;
        }
        .order-note {
          border: 1px solid #000;
          padding: 4px;
          margin: 4px 0;
        }
        .order-note-title {
          font-size: 8pt;
          font-weight: bold;
          margin: 0 0 2px 0;
          color: #000;
        }
        .order-note-text {
          font-size: 9pt;
          margin: 0;
          font-style: italic;
        }
        .footer {
          text-align: center;
          margin-top: 4px;
          padding-top: 4px;
          border-top: 1px dashed #000;
        }
        .footer p {
          margin: 2px 0;
          font-size: 9pt;
          color: #000;
          font-weight: bold;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${receiptData.isCancellationOrder ? '❌ İPTAL SİPARİŞ ❌' : receiptData.isAdditionalOrder ? '⚠️ İLAVE SİPARİŞ ⚠️' : 'MUTFAK SİPARİŞİ'}</h1>
        <div class="category-badge">${categoryName}</div>
      </div>
      
      <div class="order-info">
        ${receiptData.tableName ? `<div class="table-name">${receiptData.tableName}</div>` : ''}
        <div class="order-number">${receiptData.orderNumber}</div>
        ${receiptData.waiterName ? `<div class="waiter-name">Garson: ${receiptData.waiterName}</div>` : ''}
        <div class="order-time">${currentTime}</div>
      </div>
      
      <div class="items-section">
        ${categoryItems.map(item => `
          <div class="item">
            <div class="item-quantity">${item.quantity}</div>
            <div class="item-details">
              <p class="item-name">${item.name}</p>
              ${item.note ? `<p class="item-note">${item.note}</p>` : ''}
              ${item.subItems && item.subItems.length > 0 ? `
                <div class="sub-items">
                  ${item.subItems.map(subItem => `
                    <div class="sub-item${subItem.isExtraOption ? ' extra-option' : ''}">
                      <p class="sub-item-name">- ${subItem.name}${subItem.quantity > 1 ? ` x${subItem.quantity}` : ''}</p>
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        `).join('')}
      </div>
      
      ${receiptData.orderNote ? `
        <div class="order-note">
          <p class="order-note-title">SİPARİŞ NOTU:</p>
          <p class="order-note-text">${receiptData.orderNote}</p>
        </div>
      ` : ''}
      
      <div class="footer">
        <p>${categoryItems.length} kalem / ${categoryItems.reduce((sum, item) => sum + item.quantity, 0)} adet</p>
      </div>
    </body>
    </html>
  `;
};

// ═══════════════════════════════════════════════════════════
// 🪝 CUSTOM HOOK
// ═══════════════════════════════════════════════════════════

/**
 * Mutfak fişi yazdırma hook'u
 * SQL'den gelen ekran ayarlarını kullanarak yazıcı seçimi yapar
 */
export const useKitchenPrint = ({
  companyId,
  screenId,
  screenSettings,
  onPrintError,
  onPrintSuccess,
}: KitchenPrintServiceProps): KitchenPrintServiceReturn => {
  
  // ✅ YENİ: Yerel servis URL'sini screenSettings'den al
  // Her render'da kontrol et (debug için)
  const localServiceUrl = screenSettings?.localServiceUrl || DEFAULT_LOCAL_SERVICE_URL;
  
  // Debug: Her yazdırma işleminde URL'i logla
  useEffect(() => {
    console.log('[KitchenPrintService] 🔌 Yerel servis URL durumu:', {
      hasScreenSettings: !!screenSettings,
      localServiceUrlFromSettings: screenSettings?.localServiceUrl,
      usedUrl: localServiceUrl,
      allSettingsKeys: screenSettings ? Object.keys(screenSettings).slice(0, 10) : [] // İlk 10 key
    });
  }, [screenSettings, localServiceUrl]);
  
  // Yazıcı ayarlarını al
  const getPrintingSettings = useCallback(() => {
    const printingJson = screenSettings?.PrintingSettingsJson;
    
    if (!printingJson) {
      console.warn('[KitchenPrintService] PrintingSettingsJson bulunamadı');
      return null;
    }
    
    try {
      const settings = typeof printingJson === 'string'
        ? JSON.parse(printingJson)
        : printingJson;
      
      console.log('[KitchenPrintService] ✅ Yazdırma ayarları yüklendi:', {
        printingMode: settings.printingMode,
        defaultPrinter: settings.defaultReceiptPrinter?.name,
        definedPrinters: settings.definedPrinters?.length
      });
      
      return settings;
    } catch (error) {
      console.error('[KitchenPrintService] Yazdırma ayarları parse hatası:', error);
      return null;
    }
  }, [screenSettings?.PrintingSettingsJson]);

  /**
   * Tek yazıcıya mutfak fişi yazdır
   */
  const printKitchenReceipt = useCallback(async (receiptData: KitchenReceiptData): Promise<void> => {
    console.log('[KitchenPrintService] 🖨️ Mutfak fişi yazdırılıyor...', {
      orderNumber: receiptData.orderNumber,
      tableName: receiptData.tableName,
      itemCount: receiptData.items.length
    });

    const settings = getPrintingSettings();
    
    if (!settings) {
      const errorMsg = 'Yazdırma ayarları bulunamadı. Lütfen ekran ayarlarından yazıcı tanımlayın.';
      console.error('[KitchenPrintService] ❌', errorMsg);
      onPrintError?.(errorMsg);
      return;
    }

    // Varsayılan yazıcıyı kontrol et
    if (!settings.defaultReceiptPrinter) {
      const errorMsg = 'Varsayılan yazıcı ayarlanmamış. Lütfen ekran ayarlarından yazıcı seçin.';
      console.error('[KitchenPrintService] ❌', errorMsg);
      onPrintError?.(errorMsg);
      return;
    }

    try {
      // Yazdırma isteği gönder (ESC/POS metin modu)
      console.log('[KitchenPrintService] 📤 Yazdırma isteği gönderiliyor:', {
        printerName: settings.defaultReceiptPrinter.name,
        printerId: settings.defaultReceiptPrinter.id
      });

      const rds = settings?.receiptDisplaySettings;
      const escPosData = {
        isKitchenReceipt: true,
        items: receiptData.items.map(it => ({
          name: it.name,
          quantity: it.quantity,
          unitPrice: 0,
          totalPrice: 0,
          note: it.note,
          subItems: it.subItems
        })),
        orderNumber: receiptData.orderNumber,
        tableName: receiptData.tableName,
        waiterName: receiptData.waiterName,
        companyName: receiptData.companyName || rds?.companyName,
        orderTime: receiptData.orderTime,
        customerNote: receiptData.orderNote,
        invoiceNumber: receiptData.orderNumber,
        isAdditionalOrder: receiptData.isAdditionalOrder,
        isCancellationOrder: receiptData.isCancellationOrder,
        headerType: rds?.headerType,
        logoUrl: rds?.logoUrl,
        logoMaxWidthPx: rds?.logoMaxWidthPx ?? 384,
        logoMaxHeightPx: rds?.logoMaxHeightPx ?? 150,
        companyInfo: rds ? { name: rds.companyName || '', address: rds.companyAddress || '', taxNumber: rds.taxNumber || '' } : undefined,
      };
      await sendPrintReceipt({
        printerId: settings.defaultReceiptPrinter.id,
        printerName: settings.defaultReceiptPrinter.name,
        receiptData: escPosData
      }, { baseUrl: localServiceUrl });

      console.log('[KitchenPrintService] ✅ Mutfak fişi başarıyla yazdırıldı');
      onPrintSuccess?.(`Mutfak fişi yazdırıldı: ${receiptData.orderNumber}`);
    } catch (error: any) {
      console.error('[KitchenPrintService] ❌ Yazdırma hatası:', error);
      onPrintError?.(`Mutfak fişi yazdırılamadı: ${error.message}`);
    }
  }, [getPrintingSettings, onPrintError, onPrintSuccess, localServiceUrl]);

  /**
   * Kategori bazlı mutfak fişi yazdır
   * Her kategori için farklı yazıcıya gönderir (ayarlara göre)
   *
   * ✅ YENİ DAVRANIŞ (Kategori Modu):
   *  - Kategorisinde YAZICI ATANMAMIŞ ürünler ARTIK adisyon/varsayılan yazıcıya
   *    fallback YAPMAZ. Bunun yerine "skip" edilir (hiç yazdırılmaz).
   *  - Sonuç olarak {printedItems, skippedItems, skippedCategoryNames, allSkipped}
   *    döndürülür. Çağıran taraf bu bilgiye göre kullanıcıya uyarı modalı
   *    (tek-ürün durumu) veya bilgi modalı (kısmi durum) gösterir.
   *
   * NOT: Tek yazıcı modunda (settings.printingMode !== 'category') eski davranış
   * korunur — tüm ürünler defaultReceiptPrinter'a basılır.
   */
  const printKitchenReceiptByCategory = useCallback(async (receiptData: KitchenReceiptData): Promise<KitchenPrintResult> => {
    console.log('[KitchenPrintService] 🖨️ Kategori bazlı mutfak fişi yazdırılıyor...', {
      orderNumber: receiptData.orderNumber,
      tableName: receiptData.tableName,
      itemCount: receiptData.items.length
    });

    const emptyResult: KitchenPrintResult = {
      printedItems: [],
      skippedItems: [],
      skippedCategoryNames: [],
      allSkipped: false,
      nothingToPrint: receiptData.items.length === 0,
    };

    if (receiptData.items.length === 0) {
      console.warn('[KitchenPrintService] ⚠️ Yazdırılacak ürün yok');
      return emptyResult;
    }

    const settings = getPrintingSettings();

    if (!settings) {
      // Ayar yoksa tek yazıcı moduna düş (eski davranış)
      console.warn('[KitchenPrintService] ⚠️ Ayar bulunamadı, tek yazıcı moduna düşülüyor');
      await printKitchenReceipt(receiptData);
      return { ...emptyResult, printedItems: receiptData.items };
    }

    // Yazdırma modu kontrolü — tek yazıcı modunda eski davranış (skip mantığı YOK)
    if (settings.printingMode !== 'category') {
      console.log('[KitchenPrintService] ℹ️ Tek yazıcı modu aktif');
      await printKitchenReceipt(receiptData);
      return { ...emptyResult, printedItems: receiptData.items };
    }

    const categoryAssignments = settings.categoryPrinterAssignments || {};
    // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel) — fiş her iki yazıcıya da gider
    const categoryAssignments2 = settings.categoryPrinterAssignments2 || {};

    console.log('[KitchenPrintService] 🔍 DEBUG - categoryAssignments:', JSON.stringify(categoryAssignments));
    console.log('[KitchenPrintService] 🔍 DEBUG - items categoryId:', JSON.stringify(receiptData.items.map(item => ({
      name: item.name,
      categoryId: item.categoryId || '(BOŞ)',
      matchesAssignment: item.categoryId ? !!categoryAssignments[item.categoryId] : false
    }))));

    // Yazıcı bazlı gruplama: aynı yazıcıya atanan ürünler tek fişte birleşir
    const printerItemsMap: { [printerId: string]: KitchenOrderItem[] } = {};
    const skippedItems: KitchenOrderItem[] = []; // ✅ YENİ: artık fallback YOK, skip edilir
    const printedItems: KitchenOrderItem[] = [];

    receiptData.items.forEach(item => {
      const catId = item.categoryId;
      const printerId = catId ? categoryAssignments[catId] : undefined;
      // ✅ YENİ: 2. yazıcı atanmışsa aynı ürün o yazıcının fişine de eklenir
      const printerId2 = catId ? categoryAssignments2[catId] : undefined;

      if (catId && (printerId || printerId2)) {
        if (printerId) {
          if (!printerItemsMap[printerId]) printerItemsMap[printerId] = [];
          printerItemsMap[printerId].push(item);
        }
        if (printerId2 && printerId2 !== printerId) {
          if (!printerItemsMap[printerId2]) printerItemsMap[printerId2] = [];
          printerItemsMap[printerId2].push(item);
        }
        printedItems.push(item);
      } else {
        // ❌ Kategori atanmamış veya kategoriID yok → SKIP (yazdırılmaz)
        skippedItems.push(item);
      }
    });

    // Skip edilen kategorilerin benzersiz adları
    const skippedCategoryNamesSet = new Set<string>();
    skippedItems.forEach(it => {
      if (it.categoryName) {
        skippedCategoryNamesSet.add(it.categoryName);
      } else {
        skippedCategoryNamesSet.add('(Kategorisi olmayan)');
      }
    });
    const skippedCategoryNames = Array.from(skippedCategoryNamesSet);

    console.log('[KitchenPrintService] 📂 Yazıcı bazlı dağılım (yeni: skip mantığı):', {
      yazdirilanUrunSayisi: printedItems.length,
      skipEdilenUrunSayisi: skippedItems.length,
      yaziciSayisi: Object.keys(printerItemsMap).length,
      skipKategoriler: skippedCategoryNames,
    });

    const rds = settings?.receiptDisplaySettings;
    const buildEscPosData = (items: KitchenOrderItem[]) => ({
      isKitchenReceipt: true,
      items: items.map(it => ({ name: it.name, quantity: it.quantity, unitPrice: 0, totalPrice: 0, note: it.note, subItems: it.subItems })),
      orderNumber: receiptData.orderNumber,
      tableName: receiptData.tableName,
      waiterName: receiptData.waiterName,
      companyName: receiptData.companyName || rds?.companyName,
      orderTime: receiptData.orderTime,
      customerNote: receiptData.orderNote,
      invoiceNumber: receiptData.orderNumber,
      isAdditionalOrder: receiptData.isAdditionalOrder,
      isCancellationOrder: receiptData.isCancellationOrder,
      headerType: rds?.headerType,
      logoUrl: rds?.logoUrl,
      logoMaxWidthPx: rds?.logoMaxWidthPx ?? 384,
      logoMaxHeightPx: rds?.logoMaxHeightPx ?? 150,
      companyInfo: rds ? { name: rds.companyName || '', address: rds.companyAddress || '', taxNumber: rds.taxNumber || '' } : undefined,
    });

    const printPromises: Promise<any>[] = [];

    Object.entries(printerItemsMap).forEach(([printerId, itemsForPrinter]) => {
      const printerName = printerId;
      const escPosData = buildEscPosData(itemsForPrinter);
      console.log(`[KitchenPrintService] 📤 Yazıcıya gönderiliyor: ${printerName} (${itemsForPrinter.length} ürün)`);
      printPromises.push(
        sendPrintReceipt({
          printerId,
          printerName,
          receiptData: escPosData
        }, { baseUrl: localServiceUrl })
      );
    });

    // ❌ KALDIRILDI: defaultItems → adisyonPrinter/defaultReceiptPrinter fallback
    // Artık atanmamış ürünler hiçbir yere gönderilmez (kullanıcı isteği).
    if (skippedItems.length > 0) {
      console.warn(
        `[KitchenPrintService] ⚠️ ${skippedItems.length} ürün kategori atamasız → SKIP (yazdırılmadı):`,
        skippedItems.map(i => `${i.name} [${i.categoryName || '?'}]`).join(', ')
      );
    }

    try {
      if (printPromises.length > 0) {
        await Promise.all(printPromises);
        console.log('[KitchenPrintService] ✅ Yazdırılan fiş sayısı:', printPromises.length);
        const successMsg = skippedItems.length > 0
          ? `${printPromises.length} mutfak fişi yazdırıldı, ${skippedItems.length} ürün atandığı kategoride yazıcı olmadığı için atlandı`
          : `${printPromises.length} adet mutfak fişi yazdırıldı`;
        onPrintSuccess?.(successMsg);
      } else {
        console.warn('[KitchenPrintService] ⚠️ Yazdırılacak fiş yok — kategorilerin hiçbirine yazıcı atanmamış');
        // Hata snackbar'ı GÖSTERME — çağıran taraf modal göstereceği için
      }
    } catch (error: any) {
      console.error('[KitchenPrintService] ❌ Kategori bazlı yazdırma hatası:', error);
      onPrintError?.(`Mutfak fişleri yazdırılamadı: ${error.message}`);
    }

    return {
      printedItems,
      skippedItems: skippedItems.map(it => ({
        name: it.name,
        quantity: it.quantity,
        categoryName: it.categoryName,
        categoryId: it.categoryId,
      })),
      skippedCategoryNames,
      allSkipped: printedItems.length === 0 && skippedItems.length > 0,
      nothingToPrint: false,
    };
  }, [getPrintingSettings, printKitchenReceipt, onPrintError, onPrintSuccess, localServiceUrl]);

  return {
    printKitchenReceipt,
    printKitchenReceiptByCategory,
  };
};

// ═══════════════════════════════════════════════════════════
// 📤 EXPORTS
// ═══════════════════════════════════════════════════════════

export default useKitchenPrint;

